import { useEffect, useRef } from 'react';
import { useDocumentStore } from '@/store/documentStore';
import { useEditorStore } from '@/store/editorStore';
import { ClipboardHandler } from './ClipboardHandler';

/**
 * React hook that creates a ClipboardHandler and attaches clipboard/drop
 * event listeners. Cleans up on unmount.
 *
 * Should be called once, typically in the top-level App or Canvas component.
 */
export function useClipboard(): void {
  const handlerRef = useRef<ClipboardHandler | null>(null);

  useEffect(() => {
    const handler = new ClipboardHandler(
      () => useEditorStore.getState().selectedNodeIds[0],
      () => useDocumentStore.getState().mapDoc,
      () => {
        // mapId is not stored in the document store. We read the meta rootNodeId
        // as a proxy -- if a map is loaded, the root node exists.
        // The actual mapId is managed at the App level. We expose it via a
        // simple approach: read from the URL or a global.
        return getMapIdFromState();
      },
      () => useDocumentStore.getState().nodes,
      () => useEditorStore.getState().mode === 'editing',
      (_nodeId: string) => {
        // onUploadStart -- could set a loading indicator on the node
        // For now, this is a no-op placeholder.
      },
      (_nodeId: string) => {
        // onUploadComplete
      },
      (_nodeId: string, _error: string) => {
        // onUploadError -- could show a toast notification
        console.error(`Attachment upload failed for node ${_nodeId}: ${_error}`);
      },
    );

    handlerRef.current = handler;

    // Attach clipboard listeners on window, drop listeners on body
    // (the SVG canvas is inside the body and events bubble up)
    const detach = handler.attach(document.body);

    return detach;
  }, []);
}

// ── Helpers ─────────────────────────────────────────────────────

/**
 * Retrieve the current map ID. The App component stores this in component
 * state, so we expose it via a module-level variable that App can set.
 */
let currentMapId: string | null = null;

export function setClipboardMapId(mapId: string | null): void {
  currentMapId = mapId;
}

function getMapIdFromState(): string | null {
  return currentMapId;
}
